import { Translation } from '../common/i18n.js'
import { t } from '../common/i18n.js'
import { hasAnyAdmin, hasPerm, type PortalPermissionCategory } from './portal-role.js'
import type { PortalPermissions } from './views/portal/utils/portal-api.js'

/**
 * Visibility requirement for a nav item: a concrete permission key, or
 * 'anyAdmin' (at least one admin capability — e.g. the settings hub, which
 * gates its tabs per key itself). Items without a gate are visible to all.
 */
export type NavGate = { category: PortalPermissionCategory; key: string } | 'anyAdmin'

export interface NavItem {
    path: string
    label: Translation
    gate?: NavGate
    external?: boolean
}

export interface NavGroup {
    label?: Translation
    items: NavItem[]
}

export const passesGate = (gate: NavGate | undefined, permissions: PortalPermissions): boolean =>
    gate === undefined
        ? true
        : gate === 'anyAdmin'
          ? hasAnyAdmin(permissions)
          : hasPerm(permissions, gate.category, gate.key)

/** Filter items by held permissions, then drop any group left with no items. */
export const visibleGroups = (groups: NavGroup[], permissions: PortalPermissions): NavGroup[] =>
    groups
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => passesGate(item.gate, permissions)),
        }))
        .filter((group) => group.items.length > 0)

/** Filter a flat tab list by held permissions (e.g. the header tabs). */
export const visibleTabs = (tabs: NavItem[], permissions: PortalPermissions): NavItem[] =>
    tabs.filter((tab) => passesGate(tab.gate, permissions))

/** Root matches only itself; every other item matches its path and nested sub-routes. */
export const isItemActive = (item: NavItem, pathname: string): boolean => {
    const itemPath = item.path.split('?')[0]
    if (itemPath === '/') return pathname === '/'
    // The Me root (Andmed) is an exact match so it doesn't also light up on
    // /me/tasks etc. — those have their own items, and activeItemPath() picks
    // the longest match for the sidebar highlight.
    if (itemPath === '/me') return pathname === '/me'
    return pathname === itemPath || pathname.startsWith(itemPath + '/')
}

/** The most specific (longest) item path that matches, or null. */
export const activeItemPath = (groups: NavGroup[], pathname: string): string | null => {
    const matches = groups
        .flatMap((group) => group.items)
        .filter((item) => isItemActive(item, pathname))
        .map((item) => item.path)
        .sort((a, b) => b.length - a.length)
    return matches[0] ?? null
}

/**
 * Header navigation tabs. These are the primary navigation in the redesigned
 * top bar (icon+text), and will eventually replace the sidebar nav entirely.
 * Order here is the order shown in the header. Icons are mapped by path in the
 * shell (see `navIcons`).
 */
export const topTabs: NavItem[] = [
    { path: '/', label: t.portal.tabs.home },
    { path: '/calendar', label: t.portal.tabs.away },
    { path: '/people', label: t.portal.tabs.people },
    { path: '/events', label: t.portal.tabs.events },
    {
        path: '/reports',
        label: t.portal.tabs.reports,
        gate: { category: 'workspace', key: 'access_reports' },
    },
]

export const navGroups: NavGroup[] = [
    {
        label: t.portal.nav.meSection,
        items: [
            { path: '/', label: t.portal.nav.myDashboard },
            { path: '/me/time-away', label: t.portal.nav.myAbsences },
            { path: '/me/tasks', label: t.portal.nav.myTasks },
            { path: '/me/balancetime', label: t.portal.nav.myWorktime },
            { path: '/me/worktime', label: t.portal.nav.myProjects },
            { path: '/me/assets', label: t.portal.nav.myAssets },
            { path: '/me/documents', label: t.portal.nav.myDocuments },
            { path: 'google-drive-external', label: t.portal.nav.myThings, external: true },
            { path: '/me', label: t.portal.nav.myData },
            { path: '/me/settings', label: t.portal.nav.mySettings },
        ],
    },
    {
        label: t.portal.nav.peopleSection,
        items: [
            { path: '/people', label: t.portal.nav.people },
            { path: '/calendar', label: t.portal.nav.absences },
        ],
    },
    {
        label: t.portal.nav.cultureSection,
        items: [
            { path: '/events', label: t.portal.nav.events },
            { path: '/badges', label: t.portal.nav.playtime },
        ],
    },
    {
        label: t.portal.nav.adminSection,
        items: [
            // Reports are a management tool, not culture (Nele/Marianne, 20.10.2026).
            {
                path: '/reports',
                label: t.portal.nav.reports,
                gate: { category: 'workspace', key: 'access_reports' },
            },
            // The audit log moved under workspace settings (/settings/audit-log).
            { path: '/settings', label: t.portal.nav.settings, gate: 'anyAdmin' },
        ],
    },
]
